import { Box, Th, Thead, Tooltip, Tr } from "@chakra-ui/react";
import { flexRender, Header, SortDirection, Table } from "@tanstack/react-table";
import { RiArrowDownSLine, RiArrowUpSLine, RiExpandUpDownLine } from "react-icons/ri";
import { DragEndEvent, useDraggable, useDroppable } from "@dnd-kit/core";
import { MdDragIndicator } from "react-icons/md";

export type TableHeaderProps<TData> = {
    table: Table<TData>
    onDragEnd?(event: DragEndEvent): void;
    disableAllDrag?: boolean;
};

type DraggableThProps<TData> = {
    header: Header<any, any>;
    children: React.ReactNode;
    table: Table<TData>;
    disableAllDrag?: boolean;
}

const DraggableTh = <TData,>({ header, children, disableAllDrag }: DraggableThProps<TData>) => {
  const { setNodeRef: setDragRef, attributes, listeners, isDragging } = useDraggable({
    id: header.id,
  });

  const isMetaDisabled = header.column.columnDef.meta?.disableDrag === true;
    const disabled = disableAllDrag || isMetaDisabled;

  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: header.id,
    disabled: disabled
  });

  const leafHeaders = header.getLeafHeaders?.() ?? [header];

  const isPinnedLeft = leafHeaders.every(h => h.column.getIsPinned?.() === 'left');
  const isPinnedRight = leafHeaders.every(h => h.column.getIsPinned?.() === 'right');

  const siblingHeaders = header.headerGroup.headers;

  let leftOffset: number | undefined = undefined;
  if (isPinnedLeft) {
    const idx = siblingHeaders.findIndex(h => h.id === leafHeaders[0].id);
    leftOffset = siblingHeaders
      .slice(0, idx)
      .filter(h => h.column.getIsPinned?.() === 'left')
      .reduce((acc, h) => acc + (h.getSize?.() || 0), 0);
  }

  let rightOffset: number | undefined = undefined;
  if (isPinnedRight) {
    const idx = siblingHeaders.findIndex(h => h.id === leafHeaders[leafHeaders.length - 1].id);
    rightOffset = siblingHeaders
      .slice(idx + 1)
      .filter(h => h.column.getIsPinned?.() === 'right')
      .reduce((acc, h) => acc + (h.getSize?.() || 0), 0);
  }

  const totalChildWidth = leafHeaders.reduce((sum, leaf) => sum + (leaf.getSize?.() || 0), 0);

  return (
    <Th
      ref={(node) => {
        setDragRef(node);
        if (!isPinnedLeft && !isPinnedRight) setDropRef(node);
      }}
      style={{
        opacity: isDragging ? 0.5 : 1,
        backgroundColor: isOver ? "#f0f0f0" : "#fff",
        // cursor: isPinnedLeft || isPinnedRight ? "default" : "grab",
        position: "sticky",
        top: 0,
        left: isPinnedLeft ? leftOffset : undefined,
        right: isPinnedRight ? rightOffset : undefined,
        zIndex: isPinnedLeft || isPinnedRight ? 13 + header.depth : 12,
        width: header.subHeaders?.length ? totalChildWidth : header.getSize(),
        ...(header.column.columnDef.meta?.headerStyle ?? {}),
      }}
      textTransform="none"
      colSpan={header.colSpan}
      id={header.id}
      paddingInlineEnd="15px"
      paddingInlineStart="15px"
    >
      <Box display="flex" alignItems="center" gap="1px">
        {/* Drag handle only visible if not pinned */}
        {!disabled && !isPinnedLeft && !isPinnedRight && (
          <Box
            {...listeners}
            {...attributes}
            cursor="grab"
            display="flex"
            alignItems="center"
            justifyContent="center"
            px="1"
          >
            <MdDragIndicator size={16} />
          </Box>
        )}

        {/* Actual header content (including sort arrows, etc.) */}
        <Box flex="1" overflow="hidden" minWidth="0">
          {children}
        </Box>
      </Box>
    </Th>
  );
};

const TableHeader = <TData, >(props: TableHeaderProps<TData>) => {
    const { table } = props;

    const onHandleResize = (header: Header<TData, unknown>) => (e: React.MouseEvent | React.TouchEvent) => {
        e.preventDefault();
        e.stopPropagation();
        header.getResizeHandler()(e);
    };

    const getArrow = (sortBy: false | SortDirection) => {
        if (sortBy === 'asc') {
            return <RiArrowUpSLine size={16} />;
        } if (sortBy === 'desc') { 
            return <RiArrowDownSLine size={16} />;
        } else {
            return <RiExpandUpDownLine size={16} />;
        }
    };
  
    return (
        <Thead
            backgroundColor="white"
        >
            {table.getHeaderGroups().map((headerGroup) => (
                <Tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                        const tempHeader = header.isPlaceholder ? null : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                        );
                        const showTooltip = header.column.columnDef.meta?.showHeaderTooltip ?? true;
                        const customerHeaderRender = header.column.columnDef.meta?.customHeaderRender;

                        return (
                            <DraggableTh 
                                key={header.id} 
                                header={header} 
                                table={table}
                                disableAllDrag={props.disableAllDrag}
                            >
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    gap="10px"
                                    fontSize="14px"
                                >
                                    {showTooltip ? (
                                        <Tooltip 
                                            hasArrow={true}
                                            label={tempHeader}
                                            placement="top"
                                        >
                                            <Box
                                                textOverflow="ellipsis"
                                                overflow="hidden"
                                                whiteSpace="nowrap"
                                                flex="1"
                                                __css={{
                                                    'span': {
                                                        display: 'block',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        width: '100%',
                                                    }
                                                }}
                                            >
                                                {customerHeaderRender ?? tempHeader}
                                            </Box>
                                        </Tooltip>
                                    ) : (
                                        <Box
                                            textOverflow="ellipsis"
                                            overflow="hidden"
                                            whiteSpace="nowrap"
                                            flex="1"
                                            __css={{
                                                'span': {
                                                    display: 'block',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    width: '100%',
                                                }
                                            }}
                                        >
                                            {customerHeaderRender ?? tempHeader}
                                        </Box>
                                    )}
                                    {header.column.getCanSort() && (
                                        <Box
                                            onClick={() => {
                                                if (header.column.getIsSorted() === 'asc') {
                                                    header.column.toggleSorting(true);
                                                } else if (header.column.getIsSorted() === 'desc') {
                                                    header.column.toggleSorting();
                                                } else {
                                                    header.column.toggleSorting(false);
                                                }
                                            }}
                                            cursor={header.column.getCanSort() ? 'pointer' : 'default'}
                                        >
                                            {getArrow(header.column.getIsSorted())}
                                        </Box>
                                    )}
                                </Box>
                                {header.column.getCanResize() && header.depth === 1 ? (
                                    <Box
                                        cursor="ew-resize"
                                        position="absolute"
                                        height="10px"
                                        borderRight="2px solid"
                                        borderColor="#d7d3d3"
                                        top="50%"
                                        transform="translateY(-50%)"
                                        right="10px"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                        }}
                                        onMouseDown={onHandleResize(header)}
                                        onTouchStart={onHandleResize(header)}
                                    />
                                ) : null}
                            </DraggableTh>
                        )
                    })}
                </Tr>
            ))}
        </Thead>
    );
};

export default TableHeader;